import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { DashboardShell } from '../dashboardShared/components/DashboardShell';
import { CommandHeader } from '../dashboardShared/components/CommandHeader';
import { KpiStrip } from '../dashboardShared/components/KpiStrip';
import { WeeklyBarChart } from '../dashboardShared/components/WeeklyBarChart';
import { AttentionList } from '../dashboardShared/components/AttentionList';
import { ActivityList } from '../dashboardShared/components/ActivityList';
import { JumpToList } from '../dashboardShared/components/JumpToList';
import { ReorderCard } from '../dashboardShared/components/ReorderCard';
import { deriveReorderCard } from './deriveReorderCard';
import { isReorderPlanLocked } from '../../components/reorder/reorderApi';
import { Stack } from '@mui/material';
import { DashboardEditBar } from '../dashboardShared/components/DashboardEditBar';
import { WidgetGrid } from '../dashboardShared/components/WidgetGrid';
import { useDashboardLayout } from '../dashboardShared/useDashboardLayout';
import { lockedKpiIds, windowToWeeks } from '../dashboardShared/widgetLayout';
import { DEFAULT_STORE_KPIS, STORE_KPI_CATALOG } from '../dashboardShared/kpiCatalog';
import { useLocale } from '../../contexts/LocaleContext';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { usePlanSurface } from '../../components/planLock/usePlanSurface';
import { LockedFeature } from '../../components/planLock/LockedFeature';
import { useTranslation } from 'react-i18next';
import { bucketIntoWeeks } from '../dashboardShared/weeklyBuckets';
import type { KpiCellData, ActivityRow, JumpToRow, CommandHeaderChip } from '../dashboardShared/types';
import { countPriceMismatchFindings } from './priceMismatchCount';
import { deriveStoreAttention, shopifyPriceGapClause } from './deriveStoreAttention';
import { deriveStoreSyncHealth, pickLatestCompletedSyncRun } from './deriveStoreSyncHealth';
import { pickLatestSuccessfulSyncRun, staleSyncDays } from './syncFreshness';
import { describeNextScheduledSync } from '../../utils/syncSchedule';
import { deriveStorePlatform, platformLabel } from '../../utils/storePlatform';
import {
  useCurrentOrganization,
  useStoreCatalogCounts,
  useStoreTeamMembers,
  useStoreSalesSummary,
  useStoreRebateReport,
  useStoreIntegrations,
  useShopifyPricingAudit,
  useStoreIntegrationHistory,
  useStoreListingHolds,
  useStoreReorderSuggestions,
} from './useStoreHomeData';

const formatCents = (cents: number | undefined) => {
  if (cents == null || Number.isNaN(cents)) return '$0';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const relativeTime = (iso: string | null | undefined): string => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const diffMs = Date.now() - then;
  const hours = Math.floor(diffMs / 3600000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

/** First line of a failed run's error, short enough for one activity row. */
const failureReason = (run: { errors?: string[]; errorCount?: number }): string => {
  const first = Array.isArray(run.errors) ? run.errors.find((e) => typeof e === 'string' && e.trim()) : undefined;
  if (first) {
    const line = first.trim().split('\n')[0];
    return line.length > 90 ? `${line.slice(0, 87)}...` : line;
  }
  return run.errorCount ? `${run.errorCount.toLocaleString()} ${run.errorCount === 1 ? 'error' : 'errors'}` : 'no reason recorded';
};

/** Most distinct activity rows shown; identical neighbours fold into one "×N" row. */
const ACTIVITY_ROW_LIMIT = 5;

export const StoreHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const t = translate as unknown as (key: string, options?: Record<string, unknown>) => string;
  const { userProfile } = useAuth();
  const { isImpersonating, impersonatedOrg } = useImpersonation();

  const hasOrganization = isImpersonating
    ? !!impersonatedOrg?.id
    : !!(userProfile?.organizationId || (userProfile as any)?.primaryOrganizationId);
  const orgKey = impersonatedOrg?.id || 'self';

  // The unmapped-variant item links to the SKU Crosswalk review only where that page has it (flag `sku_crosswalk`).
  const { enabled: crosswalkEnabled } = useOrgFeatureFlag('sku_crosswalk');
  // Reorder card (flag `predictive_reorder`, plan `advancedAnalytics`): absent with the flag off.
  const { enabled: reorderEnabled } = useOrgFeatureFlag('predictive_reorder');
  const rebateSurface = usePlanSurface({ feature: 'rebateTracking' });
  const reorderSurface = usePlanSurface({ feature: 'advancedAnalytics', orgFlag: 'predictive_reorder' });
  const organizationQuery = useCurrentOrganization(orgKey, hasOrganization);
  const catalogCountsQuery = useStoreCatalogCounts(orgKey, hasOrganization);
  const teamMembersQuery = useStoreTeamMembers(orgKey, hasOrganization);
  const salesSummaryQuery = useStoreSalesSummary(orgKey, hasOrganization);
  const rebateReportQuery = useStoreRebateReport(
    orgKey,
    hasOrganization && !rebateSurface.hold && rebateSurface.mode !== 'locked',
  );
  const integrationsQuery = useStoreIntegrations(hasOrganization);
  const reorderQuery = useStoreReorderSuggestions(orgKey, hasOrganization && reorderSurface.mode === 'open');

  const organization = organizationQuery.data;
  const catalogCounts = catalogCountsQuery.data;
  const teamMembers = teamMembersQuery.data || [];
  const salesSummary = salesSummaryQuery.data;
  const rebateReport = rebateReportQuery.data;
  const integrations = integrationsQuery.data || [];
  const storePlatform = deriveStorePlatform(integrations);

  // Audit and sync-health both prefer the Shopify integration so the hero matches
  // that store's sync log. Fall back to any active integration only when there is
  // no Shopify connector (Lightspeed / BigCommerce).
  const shopifyIntegration = integrations.find((i) => i.platform === 'shopify' && i.status === 'active') || integrations.find((i) => i.platform === 'shopify');
  const shopifyIntegrationId = shopifyIntegration?.id;
  const primaryIntegration = integrations.find((i) => i.status === 'active') || integrations[0];
  const primaryIntegrationId = primaryIntegration?.id;

  const auditQuery = useShopifyPricingAudit(shopifyIntegrationId, hasOrganization);
  const listingHolds = useStoreListingHolds(integrations, hasOrganization);
  // Prefer the Shopify integration so "catalog in sync" matches that store's sync log,
  // not a Lightspeed/BigCommerce run or a MAP-audit slice.
  const syncIntegrationId = shopifyIntegrationId || primaryIntegrationId;
  const historyQuery = useStoreIntegrationHistory(syncIntegrationId, hasOrganization);

  const audit = auditQuery.data?.summary ?? null;
  const auditFindings = auditQuery.data?.findings ?? [];
  const syncRuns = historyQuery.data?.runs || [];
  const latestCompletedRun = pickLatestCompletedSyncRun(syncRuns);
  const syncHealth = deriveStoreSyncHealth({
    selectionProductCount: historyQuery.data?.selectionProductCount,
    latestCompletedRun,
  });
  const syncStatsAvailable = !!syncIntegrationId && !historyQuery.isLoading && !historyQuery.isError && syncHealth.available;
  const syncIntegration = integrations.find((i) => i.id === syncIntegrationId) || null;
  // Name of the platform the sync stats come from ("Shopify", "BigCommerce"…), never assumed.
  const syncPlatformLabel = platformLabel(syncIntegration?.platform);
  const syncInFlight = syncRuns.some((r) => r.status === 'queued' || r.status === 'running');
  // "next sync in ~3h" from the schedule the integrations list serves — null when
  // the integration is manual-only or nothing is known yet.
  const nextSync = useMemo(
    () => describeNextScheduledSync(syncIntegration?.syncSchedule, syncIntegration?.platform),
    [syncIntegration?.syncSchedule, syncIntegration?.platform]
  );

  // The last run that actually pushed products. A failed run is "activity", not a sync,
  // so it must not make a months-old store look freshly synced.
  const lastSuccessfulRun = pickLatestSuccessfulSyncRun(syncRuns);
  const lastSuccessAt = lastSuccessfulRun?.completedAt || lastSuccessfulRun?.startedAt || null;
  const staleDays =
    syncIntegrationId && !historyQuery.isLoading && !historyQuery.isError
      ? staleSyncDays(lastSuccessAt, syncIntegration?.syncSchedule)
      : null;

  const catalogTotal = catalogCounts?.total ?? 0;
  const selectedCount = syncHealth.selected;
  const liveCount = syncHealth.live;
  const blockedCount = syncStatsAvailable ? syncHealth.blocked : null;
  // Selected after the last run started (or never synced): they push on the next run.
  const pendingCount = syncStatsAvailable ? syncHealth.pending : 0;
  const pendingNote = pendingCount > 0
    ? `${pendingCount.toLocaleString()} awaiting ${syncInFlight ? 'the sync in progress' : 'next sync'}`
    : '';
  const pctInSync = syncStatsAvailable && selectedCount > 0 ? Math.round((liveCount / selectedCount) * 100) : null;
  const belowMapCount = audit?.belowMap ?? 0;
  const suspiciousDiscountCount = audit?.suspiciousDiscount ?? 0;
  const stalePriceCount = audit?.stalePrice ?? 0;
  const staleCompareAtCount = audit?.staleCompareAt ?? 0;
  const missingLivePriceCount = audit?.missingLivePrice ?? 0;
  const unmappedVariantCount = audit?.unmappedVariant ?? 0;
  // A single Shopify variant can trip more than one issue counter (below MAP and an old
  // price, say), so summing the per-category counters double-counts. The headline counts
  // distinct listings with a price problem; unmapped listings (no Motzu product to compare
  // against) are their own bucket, shown in the breakdown, and never counted here.
  const priceMismatchCount = countPriceMismatchFindings(auditFindings);
  // The price audit only exists for Shopify; other platforms show a dash, not a misleading 0.
  const priceAuditAvailable = !!shopifyIntegrationId;
  const missingImages = catalogCounts?.missingImages ?? 0;
  const missingImagesPct = catalogTotal > 0 ? Math.round((missingImages / catalogTotal) * 100) : 0;

  const loading = hasOrganization && (catalogCountsQuery.isLoading || organizationQuery.isLoading || integrationsQuery.isLoading || historyQuery.isLoading);

  const kpiLabel = (id: string) => t(`dashboardLayout.kpis.store.${id}.label`);
  const kpis: KpiCellData[] = [
    { id: 'in_sync', label: kpiLabel('in_sync'), value: hasOrganization && syncStatsAvailable ? liveCount.toLocaleString() : '-', delta: hasOrganization && pctInSync != null ? `${pctInSync}%` : '', note: hasOrganization && !syncIntegrationId ? 'Connect an integration to see sync status' : `of ${selectedCount.toLocaleString()} selected${pendingNote ? ` · ${pendingNote}` : ''}`, flag: pendingCount > 0 ? 'amber' : 'none' },
    { id: 'blocked', label: kpiLabel('blocked'), value: hasOrganization && blockedCount != null ? blockedCount.toLocaleString() : '-', delta: blockedCount != null && blockedCount > 0 ? 'fix now' : '', note: 'Not reaching storefront', flag: blockedCount != null && blockedCount > 0 ? 'red' : 'none' },
    { id: 'price_mismatch', label: kpiLabel('price_mismatch'), value: hasOrganization && priceAuditAvailable ? priceMismatchCount.toLocaleString() : '-', delta: belowMapCount > 0 ? `${belowMapCount} below MAP` : '', note: priceAuditAvailable ? 'Shopify vs Motzu price' : 'Price audit is Shopify-only', flag: priceMismatchCount > 0 ? 'red' : 'none' },
    { id: 'rebates', label: kpiLabel('rebates'), value: hasOrganization ? formatCents(rebateReport?.totalRebateAmount) : '-', delta: rebateReport?.totalUnits ? `${rebateReport.totalUnits.toLocaleString()} units` : '', note: `${rebateReport?.programCount ?? 0} programs`, flag: (rebateReport?.totalRebateAmount ?? 0) > 0 ? 'amber' : 'none' },
    { id: 'missing_images', label: kpiLabel('missing_images'), value: hasOrganization ? missingImages.toLocaleString() : '-', delta: missingImages > 0 ? `${missingImagesPct}% of catalog` : '', note: 'Blocks listing', flag: missingImages > 0 ? 'amber' : 'none' },
    { id: 'revenue_30d', label: kpiLabel('revenue_30d'), value: hasOrganization ? formatCents(salesSummary?.totalNetRevenue) : '-', delta: '', note: `${(salesSummary?.totalUnitsSold ?? 0).toLocaleString()} units · ${salesSummary?.brandCount ?? 0} brands sold`, flag: 'none' },
    { id: 'integrations', label: kpiLabel('integrations'), value: hasOrganization ? integrations.length.toString() : '-', delta: '', note: storePlatform.hasIntegration ? storePlatform.platformsLabel : 'Connected commerce integrations', flag: 'none' },
    { id: 'team', label: kpiLabel('team'), value: hasOrganization ? teamMembers.length.toString() : '-', delta: '', note: 'Users in your organization', flag: 'none' },
  ];

  const attentionItems = useMemo(
    () =>
      hasOrganization
        ? deriveStoreAttention({
            blockedCount: blockedCount ?? 0,
            liveCount,
            totalSelected: selectedCount,
            pendingCount,
            lastRunCovered: syncHealth.lastRunCovered,
            nextSyncNote: syncInFlight ? 'a sync is running now' : nextSync?.short ?? null,
            priceMismatchCount,
            belowMapCount,
            suspiciousDiscountCount,
            stalePriceCount,
            staleCompareAtCount,
            missingLivePriceCount,
            unmappedVariantCount,
            unmappedReviewHref: crosswalkEnabled ? '/app/sku-overrides?review=shopify' : null,
            rebateAmountCents: rebateReport?.totalRebateAmount ?? 0,
            rebateUnits: rebateReport?.totalUnits ?? 0,
            rebateProgramCount: rebateReport?.programCount ?? 0,
            missingImages,
            catalogTotal,
            integrationId: syncIntegrationId ?? null,
            platformLabel: syncPlatformLabel,
            listingHoldCount: listingHolds.count,
            listingHoldCountCapped: listingHolds.capped,
            listingHoldIntegrationId: listingHolds.integrationId,
            staleSyncDays: staleDays,
          })
        : [],
    [listingHolds.count, listingHolds.capped, listingHolds.integrationId, hasOrganization, blockedCount, liveCount, selectedCount, pendingCount, syncHealth.lastRunCovered, syncInFlight, nextSync, catalogTotal, priceMismatchCount, belowMapCount, suspiciousDiscountCount, stalePriceCount, staleCompareAtCount, missingLivePriceCount, unmappedVariantCount, crosswalkEnabled, rebateReport, missingImages, syncIntegrationId, syncPlatformLabel, staleDays]
  );

  const planLockedWidgetIds = useMemo(
    () => [
      ...(rebateSurface.mode === 'locked' ? ['rebates_identified'] : []),
      ...(reorderSurface.mode === 'locked' ? ['reorder'] : []),
    ],
    [rebateSurface.mode, reorderSurface.mode],
  );
  const dash = useDashboardLayout('store', organization?.id, organization?.name, planLockedWidgetIds);
  const syncChartWindow = dash.widgets.find((w) => w.id === 'sync_chart')?.config.window;
  const chartWeekCount = windowToWeeks(syncChartWindow);
  const chartWeeks = useMemo(
    () =>
      bucketIntoWeeks(
        syncRuns.map((r) => ({ at: r.startedAt, count: r.productsProcessed || 0, failed: r.status === 'error' })),
        chartWeekCount
      ),
    [syncRuns, chartWeekCount]
  );

  // Newest first. Runs that read the same ("0 products · failed" twice in a row) fold into
  // one row with a "×N" count, so a stuck sync shows once instead of filling the list.
  const activityRows: ActivityRow[] = useMemo(() => {
    const logHref = syncIntegrationId ? `/app/integrations/${syncIntegrationId}` : undefined;
    const rows: ActivityRow[] = [];
    for (const r of syncRuns) {
      const tag = r.status === 'running' || r.status === 'queued' ? 'Running' : r.status === 'error' ? 'Failed' : 'Done';
      const processed = (r.productsProcessed || 0).toLocaleString();
      const text =
        tag === 'Running'
          ? `${processed} of ${(r.productsTotal || 0).toLocaleString()} products · in progress`
          : tag === 'Failed'
            ? `${processed} products · failed · ${failureReason(r)}`
            : `${processed} products · completed`;
      const previous = rows[rows.length - 1];
      if (previous && previous.tag === tag && previous.text === text) {
        previous.count = (previous.count ?? 1) + 1;
        continue;
      }
      if (rows.length >= ACTIVITY_ROW_LIMIT) break;
      rows.push({ id: r.id, tag, text, when: relativeTime(r.completedAt || r.startedAt), href: logHref });
    }
    return rows;
  }, [syncRuns, syncIntegrationId]);

  const jumpToRows: JumpToRow[] = [
    { id: 'pricing', label: 'Pricing & MAP', meta: priceAuditAvailable ? priceMismatchCount.toLocaleString() : '', href: '/app/map-pricing-data' },
    { id: 'integrations', label: 'Integrations', meta: `${integrations.length} active`, href: '/app/integrations' },
    { id: 'sales', label: 'Sales report', meta: '30d', href: '/app/sales-report' },
    { id: 'team', label: 'Team management', meta: `${teamMembers.length} users`, href: '/app/team-management' },
  ];

  const revenueCents = salesSummary?.totalNetRevenue ?? 0;
  const rebateCents = rebateReport?.totalRebateAmount ?? 0;
  // A zero count is "nothing to report", not good news or bad news: neutral dot, not red/amber/green.
  const headerChips: CommandHeaderChip[] = [
    { label: 'Blocked products', value: (blockedCount ?? 0).toLocaleString(), dot: (blockedCount ?? 0) > 0 ? 'red' : 'slate' },
    { label: 'Price mismatches', value: priceAuditAvailable ? priceMismatchCount.toLocaleString() : '-', dot: priceAuditAvailable && priceMismatchCount > 0 ? 'red' : 'slate' },
    { label: 'Rebates identified', value: formatCents(rebateReport?.totalRebateAmount), dot: rebateCents > 0 ? 'amber' : 'slate' },
    { label: 'Revenue 30d', value: formatCents(salesSummary?.totalNetRevenue), dot: revenueCents > 0 ? 'green' : 'slate' },
    ...(staleDays != null ? [{ label: 'Last successful sync', value: relativeTime(lastSuccessAt), dot: 'amber' as const }] : []),
  ];

  const syncTimestamp = !syncIntegrationId
    ? integrationsQuery.isLoading
      ? ''
      : 'No integration connected'
    : `${syncPlatformLabel} ${lastSuccessAt ? `last successful sync ${relativeTime(lastSuccessAt)}${staleDays != null ? ' · overdue' : ''}` : 'has no successful sync yet'}${syncInFlight ? ' · sync running' : nextSync ? ` · ${nextSync.short}` : ''}`;

  const { formatCurrency } = useLocale();

  const header = (
        <CommandHeader
          roleBadge="Store"
          orgName={organization?.name || 'Your Store'}
          headlineValue={hasOrganization && pctInSync != null ? `${pctInSync}%` : '—'}
          headlineLabel="Catalog in sync"
          subline={
            !hasOrganization
              ? 'Select a store to see catalog sync status'
              : syncStatsAvailable
                ? `${liveCount.toLocaleString()} of ${selectedCount.toLocaleString()} selected products live · ${pendingNote ? `${pendingNote} · ` : ''}${(blockedCount ?? 0).toLocaleString()} blocked${priceAuditAvailable ? ` · ${shopifyPriceGapClause(priceMismatchCount)}` : ''}`
                : syncIntegrationId
                  ? `Loading ${syncPlatformLabel} sync status`
                  : 'Connect an e-commerce integration to see catalog sync status'
          }
          chips={headerChips}
          ctaLabel="Open sync log"
          onCtaClick={() => navigate(syncIntegrationId ? `/app/integrations/${syncIntegrationId}` : '/app/integrations')}
          timestamp={syncTimestamp}
          loading={loading}
        />
  );

  // Numbers the plan does not include are neither drawn nor offered while customizing; the fixed page is untouched.
  const rebateLocked = rebateSurface.mode === 'locked';
  const lockedKpiNumbers = Array.from(new Set([
    ...(dash.enabled && !dash.fixed ? lockedKpiIds('store', dash.availability) : []),
    ...(rebateLocked ? ['rebates'] : []),
  ]));
  const kpiNode = (
    <KpiStrip
      kpis={kpis}
      loading={loading}
      catalog={STORE_KPI_CATALOG}
      defaultOrder={DEFAULT_STORE_KPIS}
      order={dash.widgets.find((w) => w.id === 'kpis')?.config.kpis}
      lockedIds={lockedKpiNumbers}
      lockedNotices={rebateLocked ? { rebates: rebateSurface.planName } : undefined}
      editing={dash.editing}
      onChange={dash.setKpis}
      columns={4}
    />
  );
  const chartNode = (
    <WeeklyBarChart
      title="Products synced per week"
      totalLabel={`${liveCount.toLocaleString()} live`}
      note={`Last ${chartWeekCount} weeks · dips are failed or stalled runs`}
      weeks={chartWeeks}
      markLowAsFailure
      emptyLabel={`No sync activity in the last ${chartWeekCount} weeks`}
      unitLabel="products"
      loading={historyQuery.isLoading && hasOrganization}
    />
  );
  const attentionNode = <AttentionList items={attentionItems.slice(0, 5)} totalCount={attentionItems.length} loading={loading} />;
  const activityNode = <ActivityList title="Sync & rebate activity" rows={activityRows} loading={historyQuery.isLoading && hasOrganization} />;
  const jumpToNode = <JumpToList rows={jumpToRows} />;
  const reorderModel = useMemo(() => deriveReorderCard(reorderQuery.data), [reorderQuery.data]);
  const reorderNode = reorderSurface.mode === 'locked' ? (
    <LockedFeature variant="inline" planName={reorderSurface.planName} title={t('dashboardLayout.widgets.reorder')} />
  ) : reorderEnabled && reorderSurface.mode === 'open' ? (
    <ReorderCard
      model={reorderModel}
      loading={reorderQuery.isLoading && hasOrganization}
      planLocked={reorderQuery.isError && isReorderPlanLocked(reorderQuery.error)}
      error={reorderQuery.isError && !isReorderPlanLocked(reorderQuery.error) ? (reorderQuery.error as Error).message : null}
    />
  ) : null;
  // The fixed page has no slot of its own: the card rides under Jump to, in the right column.
  const jumpToWithReorder = reorderNode ? (
    <Stack spacing="20px">
      {jumpToNode}
      {reorderNode}
    </Stack>
  ) : jumpToNode;
  // Optional widget (plan: rebateTracking): the same report the Rebates identified KPI reads.
  const rebatesNode = rebateLocked ? (
    <LockedFeature variant="compact" planName={rebateSurface.planName} title={t('dashboardLayout.rebatesIdentifiedLabel')} />
  ) : (
    <KpiStrip
      kpis={[
        {
          id: 'rebates',
          label: t('dashboardLayout.rebatesIdentifiedLabel'),
          value: formatCurrency((rebateReport?.totalRebateAmount ?? 0) / 100),
          delta: '',
          note: t('dashboardLayout.rebatesIdentifiedNote', {
            units: (rebateReport?.totalUnits ?? 0).toLocaleString(),
            programs: rebateReport?.programCount ?? 0,
          }),
          flag: (rebateReport?.totalRebateAmount ?? 0) > 0 ? 'amber' : 'none',
        },
      ]}
      loading={rebateReportQuery.isLoading && hasOrganization}
    />
  );

  const toolbar = <DashboardEditBar controller={dash} />;

  // Nothing saved and not editing: the page exactly as it always was.
  if (!dash.enabled || dash.fixed) {
    return (
      <DashboardShell
        header={header}
        toolbar={dash.enabled ? toolbar : undefined}
        kpiStrip={kpiNode}
        chart={chartNode}
        attention={attentionNode}
        activity={activityNode}
        jumpTo={jumpToWithReorder}
      />
    );
  }

  return (
    <DashboardShell header={header} toolbar={toolbar}>
      <WidgetGrid
        shell="store"
        controller={dash}
        padding="20px 24px 22px"
        nodes={{
          kpis: kpiNode,
          sync_chart: chartNode,
          attention: attentionNode,
          activity: activityNode,
          jump_to: jumpToNode,
          rebates_identified: rebatesNode,
          ...(reorderNode ? { reorder: reorderNode } : {}),
        }}
      />
    </DashboardShell>
  );
};
